import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { SiteSurveyAction } from '../../actions/SiteSurveyAction';
import SurveyStep0 from '../../components/sitesurvey/SurveyStep0';
import SurveyStep1 from '../../components/sitesurvey/SurveyStep1';
import SurveyStep2 from '../../components/sitesurvey/SurveyStep2';
import SurveyStep3 from '../../components/sitesurvey/SurveyStep3';
import { ExportPdfReport } from '../../components/common/ExportPdfReport';
import FilePreviewModal from '../../components/common/FilePreviewModal';
import { Translate as t } from '../../config/Translate';
import AccordionSection from '../../components/common/AccordionSection';
import { AppConfig, JWT } from '../../config';
import { resolveFileUrl, downloadWithName } from '../../utils/filePreview';
import { Loading } from '../../components/common/Loading';

const TAB_KEYS = { siteInfo: 'siteInfo', productInfo: 'productInfo', deliveryInfo: 'deliveryInfo', roomDimensions: 'roomDimensions' };

const AdminSiteSurveyInfo = ({ siteSurvey, GetSiteSurvey }) => {

    const { id } = useParams();
    // 조회 완료(성공·실패) 여부. 현재 id 와 응답 _id 가 일치하면 화면을 그리고, 완료됐는데 불일치면 빈 상태.
    const [fetchDone, setFetchDone] = useState(false);
    const [showTab, setShowTab] = useState({
        [TAB_KEYS.siteInfo]: true,
        [TAB_KEYS.productInfo]: false,
        [TAB_KEYS.deliveryInfo]: false,
        [TAB_KEYS.roomDimensions]: false,
    });
    const [preview, setPreview] = useState({ show: false, file: null, url: '' });

    const toggleTab = (key) => setShowTab(prev => ({ ...prev, [key]: !prev[key] }));

    const isMatched = id === siteSurvey?._id;
    const isLoading = !isMatched && !fetchDone;

    useEffect(() => {
        let alive = true;
        if (id === siteSurvey?._id) {
            setFetchDone(true);
            return undefined;
        }
        setFetchDone(false);
        Promise.resolve(GetSiteSurvey(id))
            .catch((e) => console.warn('[AdminSiteSurveyInfo] GetSiteSurvey failed', { id, reason: e?.message || String(e) }))
            .finally(() => { if (alive) setFetchDone(true); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const apiPost = (body) => axios.post(AppConfig.API_SERVER_PATH + "/file/geturl", body, JWT());

    const handlePreview = async (att) => {
        const url = await resolveFileUrl(att, apiPost);
        if (url) {
            setPreview({ show: true, file: att, url });
        }
    };

    const handleDownload = async (att) => {
        const url = await resolveFileUrl(att, apiPost);
        if (url) {
            await downloadWithName(url, att.name);
        }
    };

    const handleDownloadAll = async (attachments) => {
        const pairs = await Promise.all(attachments.map(async (a) => [await resolveFileUrl(a, apiPost), a.name]));
        for (const [url, name] of pairs) {
            if (url) {
                await downloadWithName(url, name);
            }
        }
    };

    const closePreview = () => {
        setPreview({ show: false, file: null, url: '' });
    };

    return (
        <>
            {isLoading && <Loading />}
            {(!isLoading && !isMatched) &&
                <div className="section mt-2 text-center text-muted">{t('common.message.noData')}</div>
            }
            {isMatched &&
                <>
                    <div className="extraHeader">
                        <h2 className="mb-0">
                            {siteSurvey.siteSitename}
                            {siteSurvey.isSubmit && <span className="badge bg-success ms-2" style={{ fontSize: '0.6em' }}>Completed</span>}
                            {!siteSurvey.isSubmit && <span className="badge bg-warning ms-2" style={{ fontSize: '0.6em' }}>Step {siteSurvey.siteSurveyStep}</span>}
                        </h2>
                        <div className="ray-right">
                            <Link to="/Admin/AdminSiteSurveyList" className="btn btn-default btn-sm mt-0 me-1">{t('common.label.list')}</Link>
                        </div>
                    </div>
                    <div id="appCapsule" className="extra-header-active full-height">
                        <div className="section mt-1 mb-1">
                            <AccordionSection isOpen={showTab[TAB_KEYS.siteInfo]} onToggle={() => toggleTab(TAB_KEYS.siteInfo)} title={t('site.label.siteInfo')}>
                                <SurveyStep0 info={siteSurvey} isSummary={true} />
                            </AccordionSection>

                            <AccordionSection isOpen={showTab[TAB_KEYS.productInfo]} onToggle={() => toggleTab(TAB_KEYS.productInfo)} title={t('product.label.productInfo')}>
                                <SurveyStep1 info={siteSurvey} isSummary={true} />
                            </AccordionSection>

                            <AccordionSection isOpen={showTab[TAB_KEYS.deliveryInfo]} onToggle={() => toggleTab(TAB_KEYS.deliveryInfo)} title={t('service.label.deliveryInfo')}>
                                <SurveyStep2 info={siteSurvey} isSummary={true} />
                            </AccordionSection>

                            <AccordionSection isOpen={showTab[TAB_KEYS.roomDimensions]} onToggle={() => toggleTab(TAB_KEYS.roomDimensions)} title={t('installation.field.roomDimensions')}>
                                <SurveyStep3 info={siteSurvey} isSummary={true} onPreview={handlePreview} onDownload={handleDownload} onDownloadAll={handleDownloadAll} />
                            </AccordionSection>

                            {/* Comment & Signature */}
                            {(siteSurvey.finalComment || siteSurvey.isSignature) && (
                                <div className="section mt-1 p-2">
                                    {siteSurvey.finalComment && (
                                        <div className="mb-2">
                                            <h4 className="fw-bold mb-1">{t('common.field.comment') || 'Comment'}</h4>
                                            <p>{siteSurvey.finalComment}</p>
                                        </div>
                                    )}
                                    {siteSurvey.isSignature && (
                                        <div className="mb-2">
                                            <h4 className="fw-bold mb-1">{t('common.field.signature') || 'Signature'}</h4>
                                            <p>{siteSurvey.signatureName || '-'}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* PDF Export */}
                            <div className="mt-2 mb-3 ms-1 me-1">
                                <ExportPdfReport reportType={'sitesurvey'} infos={siteSurvey} />
                            </div>
                        </div>
                    </div>

                    <FilePreviewModal
                        show={preview.show}
                        file={preview.file}
                        url={preview.url}
                        onClose={closePreview}
                    />
                </>
            }
        </>
    );
};

const mapState = (state) => {
    const siteSurvey = state.SiteSurveyReducer.siteSurvey;
    return { siteSurvey };
}

const mapDispatch = dispatch => ({
    GetSiteSurvey: (_id) => dispatch(SiteSurveyAction.GetSiteSurvey(_id)),
})
export default connect(mapState, mapDispatch)(AdminSiteSurveyInfo);
